"use client"

import type { CSSProperties } from "react"
import { useEffect, useMemo, useState } from "react"
import Image from "next/image"
import Link from "next/link"
import ReactMarkdown from "react-markdown"
import remarkGfm from "remark-gfm"
import rehypeSanitize, { defaultSchema } from "rehype-sanitize"

import type {
  HeroButtonStyle,
  HeroContentPanel,
  HeroHeightMode,
  HeroLayoutMode,
  SliderHeroSlide,
} from "@lib/data/design"
import SafeFullAreaLink from "@modules/common/components/safe-full-area-link"
import { getHeroFillImageSizes } from "../hero-image-policy"
import { shouldRenderSliderMediaForIndex } from "./media-window"

type SliderHeroProps = {
  slides: SliderHeroSlide[]
  layoutMode?: HeroLayoutMode
  heightMode?: HeroHeightMode
}

type HeroMedia =
  | { type: "image"; src: string }
  | { type: "video"; src: string; poster?: string }

type ButtonCssVariables = CSSProperties & {
  "--hero-btn-bg"?: string
  "--hero-btn-text"?: string
  "--hero-btn-border"?: string
  "--hero-btn-hover-bg"?: string
  "--hero-btn-hover-text"?: string
  "--hero-btn-hover-border"?: string
}

const AUTOPLAY_INTERVAL_MS = 3500

const markdownSchema = {
  ...defaultSchema,
  tagNames: Array.from(new Set([...(defaultSchema.tagNames ?? []), "ins"])),
}

const resolveHeroMedia = (
  preferred: "image" | "video",
  imageUrl?: string | null,
  videoUrl?: string | null,
  posterFallback?: string | null
): HeroMedia | null => {
  const hasImage = Boolean(imageUrl)
  const hasVideo = Boolean(videoUrl)

  if (preferred === "video") {
    if (hasVideo) {
      return {
        type: "video",
        src: videoUrl as string,
        poster: imageUrl || posterFallback || undefined,
      }
    }
    if (hasImage) return { type: "image", src: imageUrl as string }
    return null
  }

  if (hasImage) return { type: "image", src: imageUrl as string }
  if (hasVideo) {
    return {
      type: "video",
      src: videoUrl as string,
      poster: imageUrl || posterFallback || undefined,
    }
  }
  return null
}

const getHeightClass = (heightMode?: HeroHeightMode) => {
  if (heightMode === "small") return "h-[42vh] min-h-[220px] md:h-[50vh]"
  if (heightMode === "half") return "h-[60vh] min-h-[220px] md:h-[65vh]"
  return "h-[85vh] min-h-[220px] md:h-[85vh]"
}

const getTextAlignClass = (textAlign: SliderHeroSlide["textAlign"]) => {
  if (textAlign === "center") return "text-center items-center"
  if (textAlign === "right") return "text-right items-end"
  return "text-left items-start"
}

const getVerticalAlignClass = (vertical: SliderHeroSlide["verticalTextAlign"]) => {
  if (vertical === "top") return "items-start"
  if (vertical === "bottom") return "items-end"
  return "items-center"
}

const RichText = ({
  value,
  className,
}: {
  value?: string | null
  className?: string
}) => {
  const content = typeof value === "string" ? value.trim() : ""
  if (!content) return null

  return (
    <ReactMarkdown
      remarkPlugins={[remarkGfm]}
      rehypePlugins={[[rehypeSanitize, markdownSchema]]}
      components={{
        p: ({ children }) => <span className={className}>{children}</span>,
        strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
        em: ({ children }) => <em className="italic">{children}</em>,
        ins: ({ children }) => <ins className="underline">{children}</ins>,
      }}
    >
      {content}
    </ReactMarkdown>
  )
}

const buttonStyleToCss = (style: HeroButtonStyle): ButtonCssVariables => {
  const backgroundColor = style.backgroundColor ?? "transparent"
  const textColor = style.textColor ?? "currentColor"
  const borderColor = style.borderColor ?? "currentColor"

  return {
    backgroundColor,
    color: textColor,
    borderColor,
    "--hero-btn-bg": backgroundColor,
    "--hero-btn-text": textColor,
    "--hero-btn-border": borderColor,
    "--hero-btn-hover-bg": style.hoverBackgroundColor ?? backgroundColor,
    "--hero-btn-hover-text": style.hoverTextColor ?? textColor,
    "--hero-btn-hover-border": style.hoverBorderColor ?? borderColor,
  }
}

const panelHasMedia = (panel: HeroContentPanel) =>
  panel.mediaType === "video" ? Boolean(panel.videoUrl) : Boolean(panel.imageUrl)

const slideHasMedia = (slide: SliderHeroSlide) =>
  slide.mediaType === "video" ? Boolean(slide.videoUrl) : Boolean(slide.imageUrl)

const normalizeSlides = (slides: SliderHeroSlide[], layoutMode: HeroLayoutMode) =>
  slides.filter((slide) => {
    if (layoutMode === "double") {
      return panelHasMedia(slide.doubleLeftPanel) && panelHasMedia(slide.doubleRightPanel)
    }
    return slideHasMedia(slide)
  })

const renderMedia = ({
  media,
  layoutMode,
  alt,
  preload,
}: {
  media: HeroMedia
  layoutMode: HeroLayoutMode
  alt: string
  preload?: boolean
}) => {
  if (media.type === "video") {
    return (
      <video
        className="absolute inset-0 h-full w-full object-cover"
        src={media.src}
        poster={media.poster}
        muted
        loop
        playsInline
        autoPlay
        preload="metadata"
        aria-hidden="true"
      />
    )
  }

  return (
    <Image
      src={media.src}
      alt={alt}
      fill
      {...(preload ? { preload: true } : { loading: "lazy" })}
      sizes={getHeroFillImageSizes(layoutMode)}
      className="object-cover"
      quality={90}
    />
  )
}

const getMediaClassName = (hideOn?: "md" | "mobile") => {
  if (hideOn === "md") {
    return "absolute inset-0 h-full w-full object-cover md:hidden"
  }
  if (hideOn === "mobile") {
    return "absolute inset-0 hidden h-full w-full object-cover md:block"
  }
  return "absolute inset-0 h-full w-full object-cover"
}

const mediaPlaceholder = ({ hideOn }: { hideOn?: "md" | "mobile" }) => (
  <div className={`${getMediaClassName(hideOn)} bg-slate-900`} aria-hidden="true" />
)

const mediaLayer = ({
  media,
  layoutMode,
  alt,
  hideOn,
  preload,
}: {
  media: HeroMedia
  layoutMode: HeroLayoutMode
  alt: string
  hideOn?: "md" | "mobile"
  preload?: boolean
}) => {
  const className = getMediaClassName(hideOn)

  if (media.type === "video") {
    return (
      <video
        className={className}
        src={media.src}
        poster={media.poster}
        muted
        loop
        playsInline
        autoPlay
        preload="metadata"
        aria-hidden="true"
      />
    )
  }

  return (
    <Image
      src={media.src}
      alt={alt}
      fill
      className={className}
      sizes={getHeroFillImageSizes(layoutMode, hideOn)}
      {...(preload ? { preload: true } : { loading: "lazy" })}
      quality={90}
    />
  )
}

const panelContent = ({
  panel,
  fallbackTextColor,
}: {
  panel: HeroContentPanel
  fallbackTextColor?: string | null
}) => {
  const hasText = Boolean(panel.title || panel.secondaryTitle || panel.subtitle || panel.ctaLabel)
  if (!hasText) return null

  return (
    <div
      className={`relative z-20 flex h-full w-full px-4 py-6 md:px-6 ${getVerticalAlignClass(
        panel.verticalTextAlign
      )}`}
    >
      <div className={`flex max-w-md flex-col gap-3 ${getTextAlignClass(panel.textAlign)}`}>
        <div className="space-y-2" style={{ color: panel.textColor ?? fallbackTextColor ?? undefined }}>
          <RichText value={panel.title} className="text-xl leading-tight md:text-4xl" />
          <RichText value={panel.secondaryTitle} className="text-base text-current/90 md:text-2xl" />
        </div>
        <RichText value={panel.subtitle} className="text-sm text-current/85 md:text-base" />
        {panel.ctaLabel && panel.ctaUrl ? (
          <Link
            href={panel.ctaUrl}
            className="inline-flex w-fit items-center gap-2 rounded-full border px-5 py-2 text-sm transition-colors duration-200 hover:bg-[var(--hero-btn-hover-bg)] hover:text-[var(--hero-btn-hover-text)] hover:border-[var(--hero-btn-hover-border)]"
            style={buttonStyleToCss(panel.buttonStyle)}
            prefetch
          >
            <RichText value={panel.ctaLabel} className="text-current" />
            <span aria-hidden>→</span>
          </Link>
        ) : null}
      </div>
    </div>
  )
}

const SliderHero = ({
  slides,
  layoutMode = "full",
  heightMode = "full",
}: SliderHeroProps) => {
  const validSlides = useMemo(() => normalizeSlides(slides, layoutMode), [slides, layoutMode])
  const total = validSlides.length
  const [activeIndex, setActiveIndex] = useState(0)
  const [paused, setPaused] = useState(false)

  useEffect(() => {
    if (total <= 1 || paused) return

    const id = setInterval(() => {
      setActiveIndex((prev) => (prev + 1) % total)
    }, AUTOPLAY_INTERVAL_MS)

    return () => clearInterval(id)
  }, [paused, total])

  useEffect(() => {
    if (activeIndex >= total) setActiveIndex(0)
  }, [activeIndex, total])

  if (total < 2) return null

  const handleNext = () => {
    setActiveIndex((prev) => (prev + 1) % total)
  }

  const handlePrevious = () => {
    setActiveIndex((prev) => (prev - 1 + total) % total)
  }

  const handleSelect = (index: number) => {
    setActiveIndex(index)
  }

  return (
    <section
      className={`relative isolate w-full overflow-hidden bg-black ${getHeightClass(heightMode)}`}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      {validSlides.map((slide, index) => {
        const isActive = index === activeIndex
        const title = slide.title?.trim()
        const secondaryTitle = slide.secondaryTitle?.trim()
        const subtitle = slide.subtitle?.trim()
        const ctaLabel = slide.ctaLabel?.trim()
        const ctaUrl = slide.ctaUrl?.trim()
        const desktopPreferredMedia = slide.mediaType === "video" ? "video" : "image"
        const desktopMedia = resolveHeroMedia(
          desktopPreferredMedia,
          slide.imageUrl,
          slide.videoUrl
        )
        const mobilePreferredMedia =
          slide.mobileMediaType === "video" || slide.mobileMediaType === "image"
            ? slide.mobileMediaType
            : desktopPreferredMedia
        const hasMobileOverride = Boolean(slide.mobileImageUrl || slide.mobileVideoUrl)
        const mobileMedia = hasMobileOverride
          ? resolveHeroMedia(
              mobilePreferredMedia,
              slide.mobileImageUrl,
              slide.mobileVideoUrl,
              slide.imageUrl
            )
          : desktopMedia
        const splitMediaByViewport =
          Boolean(hasMobileOverride && mobileMedia && desktopMedia) &&
          (mobileMedia?.type !== desktopMedia?.type ||
            mobileMedia?.src !== desktopMedia?.src ||
            (mobileMedia?.type === "video" && desktopMedia?.type === "video"
              ? (mobileMedia.poster || "") !== (desktopMedia.poster || "")
              : false))
        const media = desktopMedia
        const shouldRenderMedia = shouldRenderSliderMediaForIndex(index, activeIndex, total)

        const content = (
          <div
            key={index}
            className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
              isActive ? "z-20 opacity-100" : "z-10 opacity-0"
            }`}
            role="group"
            aria-roledescription="diapositiva"
            aria-label={`Diapositiva ${index + 1} de ${total}`}
            aria-hidden={!isActive}
          >
            {layoutMode === "double" ? (
              <div className="grid h-full w-full grid-cols-1 md:grid-cols-2">
                {[slide.doubleLeftPanel, slide.doubleRightPanel].map((panel, panelIndex) => {
                  const panelMedia = resolveHeroMedia(
                    panel.mediaType,
                    panel.imageUrl,
                    panel.videoUrl
                  )
                  if (!panelMedia) {
                    return (
                      <div
                        key={`double-slide-${index}-panel-${panelIndex}`}
                        className="relative h-full w-full bg-slate-900"
                      />
                    )
                  }
                  return (
                    <article
                      key={`double-slide-${index}-panel-${panelIndex}`}
                      className="relative h-full w-full overflow-hidden"
                    >
                      {shouldRenderMedia
                        ? renderMedia({
                            media: panelMedia,
                            layoutMode: "double",
                            alt: panel.title || `Panel ${panelIndex + 1}`,
                          })
                        : mediaPlaceholder({})}
                      <div className="absolute inset-0 bg-gradient-to-r from-black/40 via-black/20 to-transparent" />
                      {panel.ctaUrl ? (
                        <SafeFullAreaLink
                          href={panel.ctaUrl}
                          className="absolute inset-0 z-10"
                          ariaLabel={panel.ctaLabel || panel.title || `Abrir panel ${panelIndex + 1}`}
                          disabled={!isActive}
                        />
                      ) : null}
                      {panelContent({ panel, fallbackTextColor: slide.textColor })}
                    </article>
                  )
                })}
              </div>
            ) : layoutMode === "half" && media ? (
              <div className="grid h-full w-full grid-cols-1 md:grid-cols-2">
                {slide.halfTextSide === "right" ? (
                  <>
                    <div className="relative h-full w-full overflow-hidden">
                      {splitMediaByViewport && mobileMedia && desktopMedia ? (
                        <>
                          {shouldRenderMedia
                            ? mediaLayer({
                                media: mobileMedia,
                                layoutMode: "half",
                                alt: title || secondaryTitle || "Slider principal",
                                preload: index === 0,
                                hideOn: "md",
                              })
                            : mediaPlaceholder({ hideOn: "md" })}
                          {shouldRenderMedia
                            ? mediaLayer({
                                media: desktopMedia,
                                layoutMode: "half",
                                alt: title || secondaryTitle || "Slider principal",
                                preload: index === 0,
                                hideOn: "mobile",
                              })
                            : mediaPlaceholder({ hideOn: "mobile" })}
                        </>
                      ) : (
                        shouldRenderMedia
                          ? renderMedia({
                              media,
                              layoutMode: "half",
                              alt: title || secondaryTitle || "Slider principal",
                              preload: index === 0,
                            })
                          : mediaPlaceholder({})
                      )}
                    </div>
                    <div
                      className={`relative z-20 flex h-full w-full px-6 py-8 ${getVerticalAlignClass(
                        slide.verticalTextAlign
                      )}`}
                      style={{
                        backgroundColor:
                          slide.halfTextPanelBackgroundColor ?? "rgba(17, 24, 39, 1)",
                      }}
                    >
                      <div className={`flex max-w-xl flex-col gap-4 ${getTextAlignClass(slide.textAlign)}`}>
                        <div className="space-y-2" style={{ color: slide.textColor ?? "rgba(255,255,255,1)" }}>
                          <RichText value={title} className="text-xl leading-tight md:text-5xl" />
                          <RichText value={secondaryTitle} className="text-lg text-current/90 md:text-3xl" />
                        </div>
                        <RichText value={subtitle} className="text-sm text-current/85 md:text-lg" />
                        {ctaLabel && ctaUrl ? (
                          <Link
                            href={ctaUrl}
                            className="inline-flex w-fit items-center gap-2 rounded-full border px-6 py-2 text-sm transition-colors duration-200 hover:bg-[var(--hero-btn-hover-bg)] hover:text-[var(--hero-btn-hover-text)] hover:border-[var(--hero-btn-hover-border)]"
                            style={buttonStyleToCss(slide.buttonStyle)}
                            data-banner-click-ignore="true"
                            prefetch
                          >
                            <RichText value={ctaLabel} className="text-current" />
                            <span aria-hidden>→</span>
                          </Link>
                        ) : null}
                      </div>
                    </div>
                  </>
                ) : (
                  <>
                    <div
                      className={`relative z-20 flex h-full w-full px-6 py-8 ${getVerticalAlignClass(
                        slide.verticalTextAlign
                      )}`}
                      style={{
                        backgroundColor:
                          slide.halfTextPanelBackgroundColor ?? "rgba(17, 24, 39, 1)",
                      }}
                    >
                      <div className={`flex max-w-xl flex-col gap-4 ${getTextAlignClass(slide.textAlign)}`}>
                        <div className="space-y-2" style={{ color: slide.textColor ?? "rgba(255,255,255,1)" }}>
                          <RichText value={title} className="text-xl leading-tight md:text-5xl" />
                          <RichText value={secondaryTitle} className="text-lg text-current/90 md:text-3xl" />
                        </div>
                        <RichText value={subtitle} className="text-sm text-current/85 md:text-lg" />
                        {ctaLabel && ctaUrl ? (
                          <Link
                            href={ctaUrl}
                            className="inline-flex w-fit items-center gap-2 rounded-full border px-6 py-2 text-sm transition-colors duration-200 hover:bg-[var(--hero-btn-hover-bg)] hover:text-[var(--hero-btn-hover-text)] hover:border-[var(--hero-btn-hover-border)]"
                            style={buttonStyleToCss(slide.buttonStyle)}
                            data-banner-click-ignore="true"
                            prefetch
                          >
                            <RichText value={ctaLabel} className="text-current" />
                            <span aria-hidden>→</span>
                          </Link>
                        ) : null}
                      </div>
                    </div>
                    <div className="relative h-full w-full overflow-hidden">
                      {splitMediaByViewport && mobileMedia && desktopMedia ? (
                        <>
                          {shouldRenderMedia
                            ? mediaLayer({
                                media: mobileMedia,
                                layoutMode: "half",
                                alt: title || secondaryTitle || "Slider principal",
                                preload: index === 0,
                                hideOn: "md",
                              })
                            : mediaPlaceholder({ hideOn: "md" })}
                          {shouldRenderMedia
                            ? mediaLayer({
                                media: desktopMedia,
                                layoutMode: "half",
                                alt: title || secondaryTitle || "Slider principal",
                                preload: index === 0,
                                hideOn: "mobile",
                              })
                            : mediaPlaceholder({ hideOn: "mobile" })}
                        </>
                      ) : (
                        shouldRenderMedia
                          ? renderMedia({
                              media,
                              layoutMode: "half",
                              alt: title || secondaryTitle || "Slider principal",
                              preload: index === 0,
                            })
                          : mediaPlaceholder({})
                      )}
                    </div>
                  </>
                )}
              </div>
            ) : media ? (
              <>
                {splitMediaByViewport && mobileMedia && desktopMedia ? (
                  <>
                    {shouldRenderMedia
                      ? mediaLayer({
                          media: mobileMedia,
                          layoutMode: "full",
                          alt: title || secondaryTitle || "Banner principal",
                          preload: index === 0,
                          hideOn: "md",
                        })
                      : mediaPlaceholder({ hideOn: "md" })}
                    {shouldRenderMedia
                      ? mediaLayer({
                          media: desktopMedia,
                          layoutMode: "full",
                          alt: title || secondaryTitle || "Banner principal",
                          preload: index === 0,
                          hideOn: "mobile",
                        })
                      : mediaPlaceholder({ hideOn: "mobile" })}
                  </>
                ) : (
                  shouldRenderMedia
                    ? renderMedia({
                        media,
                        layoutMode: "full",
                        alt: title || secondaryTitle || "Banner principal",
                        preload: index === 0,
                      })
                    : mediaPlaceholder({})
                )}
                {ctaUrl ? (
                  <SafeFullAreaLink
                    href={ctaUrl}
                    className="absolute inset-0 z-10"
                    ariaLabel={ctaLabel || title || secondaryTitle || "Abrir banner"}
                    disabled={!isActive}
                  />
                ) : null}
                <div className="absolute inset-0 bg-gradient-to-r from-black/10 via-black/20 to-transparent" />
                <div className={`relative z-20 flex h-full w-full px-6 py-8 ${getVerticalAlignClass(slide.verticalTextAlign)}`}>
                  <div className="content-container w-full">
                    <div className={`flex max-w-4xl flex-col gap-6 px-4 md:gap-8 ${getTextAlignClass(slide.textAlign)}`}>
                      <div className="space-y-2" aria-live={isActive ? "polite" : undefined} style={{ color: slide.textColor ?? "rgba(255,255,255,1)" }}>
                        <RichText value={title} className="text-xl leading-tight md:text-6xl" />
                        <RichText value={secondaryTitle} className="text-lg text-current/90 md:text-4xl" />
                      </div>
                      <RichText value={subtitle} className="text-base text-current/80 md:text-lg" />
                      {ctaLabel && ctaUrl ? (
                        <Link
                          href={ctaUrl}
                          className="inline-flex w-fit items-center gap-3 rounded-full border px-6 py-2 text-sm transition-colors duration-200 hover:bg-[var(--hero-btn-hover-bg)] hover:text-[var(--hero-btn-hover-text)] hover:border-[var(--hero-btn-hover-border)]"
                          style={buttonStyleToCss(slide.buttonStyle)}
                          data-banner-click-ignore="true"
                          prefetch
                        >
                          <RichText value={ctaLabel} className="text-current" />
                          <span aria-hidden>→</span>
                        </Link>
                      ) : null}
                    </div>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center text-white/70">
                Sin media configurada
              </div>
            )}
          </div>
        )

        return content
      })}

      <div className="absolute inset-y-0 left-0 z-30 flex items-center px-3">
        <button
          type="button"
          onClick={handlePrevious}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          className="rounded-full bg-black/40 px-3 py-2 text-white transition hover:bg-black/60"
          data-banner-click-ignore="true"
          aria-label="Diapositiva anterior"
        >
          ‹
        </button>
      </div>
      <div className="absolute inset-y-0 right-0 z-30 flex items-center px-3">
        <button
          type="button"
          onClick={handleNext}
          onFocus={() => setPaused(true)}
          onBlur={() => setPaused(false)}
          className="rounded-full bg-black/40 px-3 py-2 text-white transition hover:bg-black/60"
          data-banner-click-ignore="true"
          aria-label="Diapositiva siguiente"
        >
          ›
        </button>
      </div>

      <div className="absolute inset-x-0 bottom-6 z-30 flex items-center justify-center gap-2">
        {validSlides.map((_, index) => {
          const isActive = index === activeIndex
          return (
            <button
              key={index}
              type="button"
              className={`h-2.5 w-2.5 rounded-full transition ${
                isActive ? "bg-white" : "bg-white/50 hover:bg-white/80"
              }`}
              aria-label={`Ir a la diapositiva ${index + 1}`}
              aria-pressed={isActive}
              onClick={() => handleSelect(index)}
              onFocus={() => setPaused(true)}
              onBlur={() => setPaused(false)}
              data-banner-click-ignore="true"
            />
          )
        })}
      </div>
    </section>
  )
}

export default SliderHero
